import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Camera, FileText, Calendar, User, Download, Eye } from 'lucide-react';
import AppHeader from '@/components/AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SearchPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [document, setDocument] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSearch = async (e) => {
    e?.preventDefault();
    
    if (!searchTerm.trim()) {
      return;
    }

    setSearching(true);
    setError('');
    setDocument(null);

    try {
      const response = await axios.get(`${API}/search/${searchTerm.trim()}`);
      setDocument(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Document not found');
    } finally {
      setSearching(false);
    }
  };

  const handleScanQR = () => {
    // For now, show a message. In production, integrate with a QR scanner library
    alert('QR Scanner: Please use your device camera to scan the QR code on the document. The QR code will redirect to the verification page.');
  };

  const handleViewDocument = () => {
    if (document) {
      navigate(`/verify/${document.verification_id}`);
    }
  };

  const handleDownload = () => {
    if (document) {
      window.open(`${API}/documents/${document.verification_id}/pdf`, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <AppHeader title="Document Search" subtitle="Search by Document ID" />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Search Bar */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Search Documents</h1>
          <p className="text-slate-600 mb-8">Enter a Document ID or scan a QR code to verify authenticity</p>

          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Enter Document ID (e.g., DOC-12345)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-14 pl-12 pr-14 text-base"
                />
                <button
                  type="button"
                  onClick={handleScanQR}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  title="Scan QR Code"
                >
                  <Camera className="w-5 h-5 text-slate-600" />
                </button>
              </div>
              <Button
                type="submit"
                disabled={searching}
                className="h-14 px-8 bg-slate-900 hover:bg-slate-800 text-white font-medium"
              >
                {searching ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center mb-8">
            <FileText className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-red-900 mb-1">Document Not Found</h3>
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Search Results */}
        {document && (
          <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">{document.document_name || 'Document'}</h2>
                <p className="text-slate-600">Document ID: {document.document_id}</p>
              </div>
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                document.validity_status === 'Valid' ? 'bg-green-100 text-green-800' :
                document.validity_status === 'Revoked' ? 'bg-red-100 text-red-800' :
                'bg-amber-100 text-amber-800'
              }`}>
                {document.validity_status}
              </span>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-3 text-slate-700">
                <FileText className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Folder</p>
                  <p className="font-medium">{document.folder_name}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-slate-700">
                <Calendar className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Issue Date</p>
                  <p className="font-medium">{new Date(document.issue_date).toLocaleDateString()}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-slate-700">
                <User className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Generated By</p>
                  <p className="font-medium">{document.generated_by}</p>
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-4 border-t">
              <Button
                onClick={handleViewDocument}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white flex items-center justify-center gap-2"
              >
                <Eye className="w-4 h-4" />
                View Full Document
              </Button>
              <Button
                onClick={handleDownload}
                variant="outline"
                className="flex-1 flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download
              </Button>
            </div>
          </div>
        )}

        {/* Instructions */}
        {!document && !error && !searching && (
          <div className="text-center text-slate-500 mt-12">
            <p className="mb-4">Enter a Document ID to search or scan a QR code from any document</p>
            <div className="flex items-center justify-center gap-8 text-sm">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4" />
                <span>Search by ID</span>
              </div>
              <div className="flex items-center gap-2">
                <Camera className="w-4 h-4" />
                <span>Scan QR Code</span>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-12">
        <div className="max-w-7xl mx-auto px-6 py-6 text-center text-sm text-slate-500">
          <p>This is an official verification page by Formium Alliance</p>
          <p className="mt-1">© 2025 Formium Alliance. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
